import { format } from 'date-fns';
import { Button, Dropdown, Empty, Popover, Tag, Tooltip } from 'antd';
import { DeleteOutlined, DownloadOutlined, MoreOutlined, ReloadOutlined } from '@ant-design/icons';
import { Table, TableColumnDefinition } from '../../../../componentsV2/Table';
import { FrameworkReportingExecution } from '../../types';
import { FC, useState } from 'react';
import { useFrameworkReporting } from '../../hooks/use-framework-reporting';
import { useAccountSearch } from '../../../account/account-search/account-search.hook';
import { useOrganizationMembers } from '../../../organizations/hooks/useOrganizationMembers';
import { coreService } from '../../../../services/core/core-service';

const statusColor: Record<string, string> = {
  processing: 'blue',
  success: 'green',
  error: 'red',
};

type ReportsTableProps = {
  onRerun: (record: FrameworkReportingExecution) => void;
  onDelete: (record: FrameworkReportingExecution) => void;
};

export const ReportsTable: FC<ReportsTableProps> = ({ onRerun, onDelete }) => {
  const { accounts } = useAccountSearch();
  const { members } = useOrganizationMembers();

  const { isLoading, frameworkReportExecutions, paginationMetadata, setPage } =
    useFrameworkReporting();

  const [downloadLinkStatus, setDownloadLinkStatus] = useState<
    Record<string, 'loading' | 'success' | 'error'>
  >({});

  const handleDownloadItem = async (record: FrameworkReportingExecution) => {
    try {
      setDownloadLinkStatus(prev => ({
        ...prev,
        [record.id]: 'loading',
      }));

      const { downloadLink } = await coreService.getFrameworkExecutionDownloadLink(record.id);

      const link = document.createElement('a');
      link.href = downloadLink;
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadLinkStatus(prev => ({
        ...prev,
        [record.id]: 'success',
      }));
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloadLinkStatus(prev => ({
        ...prev,
        [record.id]: 'error',
      }));
    }
  };

  const columns: TableColumnDefinition<FrameworkReportingExecution>[] = [
    {
      key: 'framework',
      title: 'Framework',
      dataIndex: 'framework',
    },
    {
      key: 'type',
      title: 'Type',
      dataIndex: 'type',
      render: value => (
        <Tag
          color={value === 'account' ? 'geekblue' : 'purple'}
          style={{ textTransform: 'capitalize' }}
        >
          {value}
        </Tag>
      ),
    },
    {
      key: 'from',
      title: 'Date Range',
      dataIndex: 'from',
      render: (value, record) => (
        <span>
          {format(new Date(record.from), 'MM/dd/yyyy')} -{' '}
          {format(new Date(record.to), 'MM/dd/yyyy')}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      render: value => {
        return (
          <Tooltip
            title={value === 'processing' ? "We'll email you when your report is ready." : null}
          >
            <Tag color={statusColor[value]} style={{ textTransform: 'capitalize' }}>
              {value}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      key: 'createdAt',
      title: 'Executed At',
      dataIndex: 'createdAt',
      render: value => {
        return <span>{format(new Date(value), 'MM/dd/yyyy')}</span>;
      },
    },
    {
      key: 'id',
      title: 'Accounts',
      dataIndex: 'id',
      render: (value, record) => {
        return (
          <Popover
            trigger="hover"
            content={
              <div>
                {record.type === 'account' &&
                  record.parameters.accountIds?.map(id => {
                    const account = accounts.find(account => account.id === id);
                    return <div key={id}>{account?.name}</div>;
                  })}

                {record.type === 'scorecard' &&
                  record.parameters.csmIds?.map(id => {
                    const member = members.find(member => member.user.id === id);
                    return (
                      <div key={id}>
                        {member?.user.firstName} {member?.user.lastName}
                      </div>
                    );
                  })}
              </div>
            }
          >
            <Tag
              color={record.type === 'account' ? 'blue' : 'magenta'}
              style={{ cursor: 'pointer', textTransform: 'capitalize' }}
            >
              {record.type === 'account' && (
                <>
                  <strong>{record.parameters.accountIds!.length}</strong> account
                  {record.parameters.accountIds!.length === 1 ? '' : 's'}
                </>
              )}

              {record.type === 'scorecard' && (
                <>
                  <strong>{record.parameters.csmIds!.length}</strong> CSM
                  {record.parameters.csmIds!.length === 1 ? '' : 's'}
                </>
              )}
            </Tag>
          </Popover>
        );
      },
    },
    {
      key: 'id',
      title: 'Download',
      render: (value, record) => {
        const status = downloadLinkStatus[record.id];
        return (
          <Tooltip
            title={
              record.status !== 'success'
                ? 'Download is only available when the report is processed'
                : ''
            }
          >
            <Button
              onClick={() => handleDownloadItem(record)}
              disabled={record.status !== 'success'}
              type="link"
              loading={status === 'loading'}
              style={{
                color: record.status === 'success' ? '#ff6c54' : '#d9d9d9',
                textDecoration: 'underline',
                padding: 0,
              }}
              icon={<DownloadOutlined />}
            >
              Download
            </Button>
          </Tooltip>
        );
      },
    },
    {
      key: 'id',
      title: 'Actions',
      render: (value, record) => {
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  label: (
                    <Tooltip title="You can re-run the same report with the same properties; or change them">
                      <span>Re-run</span>
                    </Tooltip>
                  ),
                  icon: <ReloadOutlined />,
                  key: 're-run',
                  onClick: () => onRerun(record),
                },
                {
                  label: 'Delete',
                  icon: <DeleteOutlined />,
                  danger: true,
                  key: 'delete',
                  onClick: () => onDelete(record),
                },
              ],
            }}
          >
            <Button type="default" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Table
      locale={{
        emptyText: isLoading ? (
          `Loading framework reports executions...`
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No framework reports executions found"
          />
        ),
      }}
      pagination={{
        pageSize: paginationMetadata?.itemsPerPage ?? 10,
        total: paginationMetadata?.totalItems ?? 0,
        current: paginationMetadata?.page ?? 1,
        onChange: page => setPage(page),
      }}
      loading={isLoading}
      columns={columns}
      data={frameworkReportExecutions ?? []}
    />
  );
};
